import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { MaskedLines, Reveal, RevealImage } from "@/components/motion";
import CTASection from "@/components/CTASection";
import { IMAGES } from "@/data/content";

const PILLARS = [
  {
    n: "01",
    title: "How I coach confidence",
    text: "Confidence isn't something you need before you start. It's something we build through training. When a weight feels scary, we don't avoid it and we don't force it. We break it down, approach it gradually and let your body collect proof that it's capable. The fear around heavy lifts usually fades the moment trust arrives.",
    items: ["Confidence built, not required", "Fear around heavy weights, worked through", "Proof your body is capable"],
  },
  {
    n: "02",
    title: "How I coach food and body image",
    text: "My studies in eating disorder psychology shape every conversation we have about food. There is no rigid dieting here, no guilt and no shame. We look at food rules, fear foods and body image honestly, and we work on your relationship with eating the same way we work on a lift, with patience and practice.",
    items: ["No rigid dieting or shame", "Food rules and fear foods, unpacked", "Body image worked on, not ignored"],
  },
  {
    n: "03",
    title: "How I work with your thinking",
    text: "Perfectionism, comparison and all-or-nothing thinking show up in training just as much as they do around food. I help you notice those patterns, understand where they come from and respond to them differently, so one missed session or one off day stops derailing everything.",
    items: ["Perfectionism, noticed and named", "Comparison, put in its place", "Off days without the spiral"],
  },
  {
    n: "04",
    title: "How I coach you towards autonomy",
    text: "The goal was never to keep you as a client forever. Everything we do, the questions I ask, the explanations I give, the decisions we make together, is practice for you making those calls yourself. Consistency over intensity, and skills that stay with you for life.",
    items: ["Understanding, not just instruction", "Skills you keep for life", "A coach you grow out of, happily"],
  },
];

const Psychology = () => {

  return (
    <main data-testid="psychology-page">
      {/* HEADER */}
      <section className="bg-ink text-white grain">
        <div className="relative z-10 mx-auto max-w-7xl px-6 lg:px-10 pt-32 md:pt-44 pb-16 md:pb-24">
          <p className="label-text text-ocean" data-testid="psychology-eyebrow">
            The Strength Movement philosophy · 02
          </p>
          <h1
            className="mt-6 font-display text-5xl sm:text-6xl lg:text-8xl leading-[0.92] tracking-tight"
            data-testid="psychology-heading"
          >
            <MaskedLines lines={["The Psychology"]} />
            <span className="block overflow-hidden pb-[0.1em]">
              <motion.span
                className="block italic font-light text-ocean text-3xl sm:text-4xl lg:text-6xl"
                initial={{ y: "115%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 1, delay: 0.45, ease: [0.22, 1, 0.36, 1] }}
              >
                Understanding how you work.
              </motion.span>
            </span>
          </h1>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.65 }}
            className="mt-8 max-w-2xl space-y-5 text-base md:text-lg leading-relaxed text-white/60"
          >
            <p>
              We have more training and nutrition information at our fingertips
              than ever, so why don't we all have the answers? Because knowing
              what to do and being able to apply it are two different things.
            </p>
            <p>
              My coaching starts with understanding why that information hasn't
              translated into clarity, confidence or momentum for you. Together,
              we look beneath your behaviours to the beliefs and patterns shaping
              them, then create a path that feels safe enough to trust, without
              constantly bouncing between new training plans and dieting advice.
            </p>
            <p>
              Because the way you operate around training and food rarely exists
              in isolation. How you respond to uncertainty, pressure and slow
              progress often reflects how you move through the rest of your life.
              Change those patterns here, and the impact can reach far beyond the
              gym.
            </p>
          </motion.div>
        </div>
      </section>

      {/* PILLARS */}
      <section className="bg-white" data-testid="psychology-pillars-section">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-24 md:py-32">
          <div className="grid gap-px border border-ink/10 bg-ink/10 md:grid-cols-2">
            {PILLARS.map((p, i) => (
              <Reveal key={p.n} delay={0.06 * i} className="bg-white p-8 md:p-12">
                <div data-testid={`psychology-pillar-${p.n}`}>
                  <span className="font-display text-5xl md:text-6xl italic font-light text-ocean/40">{p.n}</span>
                  <h2 className="mt-5 font-display text-3xl md:text-4xl">{p.title}</h2>
                  <p className="mt-4 text-sm md:text-base leading-relaxed text-ink/65">{p.text}</p>
                  <ul className="mt-7 space-y-2.5">
                    {p.items.map((item) => (
                      <li key={item} className="flex items-center gap-3 text-sm text-ink/70">
                        <span className="h-1 w-1 rounded-full bg-ocean" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* IMPORTANT NOTE */}
      <section className="bg-white" data-testid="psychology-disclaimer-section">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 pb-24 md:pb-32">
          <Reveal>
            <div className="max-w-3xl border-l-2 border-ocean bg-mist p-8 md:p-12">
              <p className="label-text text-ocean">An important note</p>
              <p className="mt-4 text-base md:text-lg leading-relaxed text-ink/75">
                I'm a strength coach informed by eating disorder psychology. I'm
                not a psychologist, and coaching with me isn't a replacement for
                clinical treatment. If you need that level of support, I'll say
                so, help you find it, and we can work alongside it where that's
                useful.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* CLIENT STORY */}
      <section className="bg-ink text-white grain" data-testid="psychology-testimonial-section">
        <div className="relative z-10 mx-auto max-w-5xl px-6 lg:px-10 py-24 md:py-36">
          <Reveal>
            <p className="label-text text-ocean">Client story</p>
            <h2 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl leading-[0.98] tracking-tight text-balance" data-testid="testimonial-heading">
              What this can look like
              <span className="italic font-light text-white/60"> in practice</span>
            </h2>
          </Reveal>

          <Reveal delay={0.1}>
            <blockquote
              className="mt-14 border-l-2 border-ocean pl-6 md:pl-10"
              data-testid="testimonial-pull-quote"
            >
              <p className="font-display text-2xl sm:text-3xl lg:text-4xl italic font-light leading-[1.25] text-white text-balance">
                “Kim has pushed me to be the strongest I have ever been, while
                helping me accept the reality of what it looks and feels like to
                maintain, instead of being in a permanent diet or cutting phase.”
              </p>
            </blockquote>
          </Reveal>

          <Reveal delay={0.15}>
            <div
              className="mt-14 md:columns-2 md:gap-12 space-y-5 text-sm md:text-base leading-relaxed text-white/70"
              data-testid="testimonial-full-text"
            >
              <p>
                “Kim has been an absolute joy to work with. She understands the
                complex relationship between emotional wellbeing and binge
                eating, providing practical support that gives me autonomy and
                empowerment throughout the healing process.
              </p>
              <p>
                Kim is realistic about the complexity of these relationships and
                has helped me recognise the importance of trusting the process,
                rather than falling into an all or nothing mentality.
              </p>
              <p>
                She understands that exploring our relationship with food also
                means developing a greater sense of presence. We have practised
                this through meaningful actions, such as mindful meditations and
                journalling the emotions that underpin bingeing or overeating in
                real time.
              </p>
              <p>
                Kim recognises that these processes take time and that every
                achievement is a success, no matter how small we might perceive
                it to be.
              </p>
              <p>
                Kim has pushed me to be the strongest I have ever been, while
                helping me accept the reality of what it looks and feels like to
                maintain, instead of being in a permanent diet or cutting phase.
              </p>
              <p>
                She allows her clients to remain in control of their own journey,
                offering the right tools when needed to encourage reflection and
                recognise what has already been achieved. This has helped me
                maintain a more positive relationship with food and training.
              </p>
              <p>I couldn’t recommend Kim more.”</p>
            </div>
            <p className="mt-10 text-xs uppercase tracking-[0.25em] text-white/40" data-testid="testimonial-attribution">
              A Strength Movement client
            </p>
          </Reveal>
        </div>
      </section>

      {/* IMAGE + QUOTE BAND */}
      <section className="bg-mist" data-testid="psychology-quote-section">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-24 md:py-32">
          <div className="grid items-center gap-12 lg:grid-cols-12">
            <div className="lg:col-span-5 order-last lg:order-first">
              <RevealImage
                src={IMAGES.kimEveryday}
                alt="Coach Kim, strength as a vehicle for self-trust"
                className="arch-clip aspect-[4/5]"
                testId="psychology-image"
              />
            </div>
            <div className="lg:col-span-7 lg:pl-8">
              <Reveal>
                <p className="font-display text-3xl sm:text-4xl lg:text-5xl leading-[1.1] tracking-tight text-balance">
                  Strength is the vehicle.
                  <span className="italic font-light text-ocean"> The destination is trust.</span>
                </p>
                <p className="mt-6 max-w-xl text-base md:text-lg leading-relaxed text-ink/70">
                  We don't just build stronger lifters. We build women who trust
                  themselves, in the gym, around food, and everywhere else.
                </p>
                <Link
                  to="/coaching"
                  data-testid="psychology-to-coaching-link"
                  className="group mt-8 inline-flex items-center gap-2 text-sm font-bold text-ink transition-colors hover:text-ocean"
                >
                  See how this becomes coaching
                  <ArrowRight size={16} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <CTASection
        title="Ready to stop"
        accent="second-guessing yourself?"
        body="Book a free 30-minute chat with Kim, a conversation about where you're at, not a sales pitch."
      />
    </main>
  );
};

export default Psychology;
